import type { ReactNode } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { canEditData } from '@repo/shared/common';
import { ensureRouteData } from '~/lib/query';
import { currentOrganizationQueryOptions } from '~/lib/queries/organization';
import { customAttributesQueryOptions } from '~/features/portfolio/use-custom-attributes';
import { standardFieldsQueryOptions } from '~/features/portfolio/use-standard-fields';
import { FieldsPage } from '~/features/portfolio/FieldsPage';

export const Route = createFileRoute('/_protected/fields/')({
  // Viewer guard: the Fields page (both tabs) is an edit surface — editing custom
  // fields and the org's value/percent interpretation. Bounce viewers to
  // /unauthorized before the loader runs.
  beforeLoad: async ({ context }) => {
    const { role } = await context.queryClient.ensureQueryData(
      currentOrganizationQueryOptions(),
    );
    if (role === null || !canEditData(role)) {
      throw redirect({ to: '/unauthorized' });
    }
  },
  // The custom-fields list defines the default (Custom) tab, so await it on the server:
  // the first paint shows the real table (or the empty state) instead of a skeleton. The
  // Standard tab's data is prefetched (non-blocking) alongside so switching to it is warm.
  loader: async ({ context }) => {
    await ensureRouteData(context.queryClient, customAttributesQueryOptions());
    void context.queryClient.prefetchQuery(standardFieldsQueryOptions());
  },
  component: FieldsRoute,
});

function FieldsRoute(): ReactNode {
  return <FieldsPage />;
}
